/**
 * QuickFacts Component
 * 
 * Displays key statistics and features in a visually prominent box.
 * Optimized for GEO (Generative Engine Optimization) with structured data
 * that AI engines can easily parse and cite.
 * 
 * All facts are verified against actual codebase counts.
 */

import { Helmet } from 'react-helmet-async';

/**
 * Quick facts data structure
 */
interface Fact {
  icon: string;
  label: string;
  value: string;
  detail?: string;
}

const facts: Fact[] = [
  {
    icon: '🎵',
    label: 'Instruments',
    value: '47',
    detail: 'Across 6 categories'
  },
  {
    icon: '⚡',
    label: 'Audio Effects',
    value: '10',
    detail: 'Professional-grade'
  },
  {
    icon: '🎹',
    label: 'Channels',
    value: '4-8',
    detail: 'Simultaneous tracks'
  },
  {
    icon: '🎼',
    label: 'BPM Range',
    value: '40-240',
    detail: 'All music styles'
  },
  {
    icon: '📤',
    label: 'Export Format',
    value: 'WAV',
    detail: 'High-quality audio'
  },
  {
    icon: '💯',
    label: 'Licensing',
    value: '100%',
    detail: 'Royalty-free'
  },
  {
    icon: '🆓',
    label: 'Pricing',
    value: 'Free',
    detail: 'To use & create'
  },
  {
    icon: '⚡',
    label: 'Platform',
    value: 'Web',
    detail: 'No installation'
  }
];

/**
 * QuickFacts component
 * Displays key statistics in a grid layout with structured data
 */
export function QuickFacts() {
  // Generate ItemList structured data for AI engines
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "8-Bit Composer Key Features",
    "description": "Quick facts and statistics about 8-Bit Composer",
    "itemListElement": facts.map((fact, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": fact.label,
      "description": `${fact.value} ${fact.detail || ''}`
    }))
  };

  return (
    <>
      {/* Structured Data for AI Engines */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <section
        aria-labelledby="quick-facts-heading"
        style={{
          maxWidth: '1200px',
          margin: '3rem auto',
          padding: '0 1rem',
          width: '88%'
        }}
      >
        <div
          style={{
            background: 'var(--color-surface)',
            border: '2px solid var(--color-accent)',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
          }}
        >
          {/* Heading */}
          <h2
            id="quick-facts-heading"
            style={{
              textAlign: 'center',
              color: 'var(--color-accent)',
              fontSize: '1.5rem',
              fontWeight: 'bold',
              marginBottom: '0.5rem',
              letterSpacing: '0.5px'
            }}
          >
            📊 8-Bit Composer at a Glance
          </h2>
          
          <div
            style={{
              width: '60px',
              height: '3px',
              background: 'var(--color-accent)',
              margin: '0 auto 2rem',
              borderRadius: '2px'
            }}
          />

          {/* Facts Grid */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '1.5rem'
            }}
          >
            {facts.map((fact, index) => (
              <div
                key={index}
                style={{
                  textAlign: 'center',
                  padding: '1rem',
                  background: 'var(--color-bg-secondary)',
                  borderRadius: '8px',
                  border: '1px solid var(--color-border)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                {/* Icon */}
                <div
                  style={{
                    fontSize: '2rem',
                    marginBottom: '0.5rem',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))'
                  }}
                >
                  {fact.icon}
                </div>

                {/* Value */}
                <div
                  style={{
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: 'var(--color-accent)',
                    marginBottom: '0.25rem'
                  }}
                >
                  {fact.value}
                </div>

                {/* Label */}
                <div
                  style={{
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    color: 'var(--color-fg)',
                    marginBottom: '0.25rem'
                  }}
                >
                  {fact.label}
                </div>

                {/* Detail */}
                {fact.detail && (
                  <div
                    style={{
                      fontSize: '0.75rem',
                      color: 'var(--color-fg-secondary)',
                      opacity: 0.8
                    }}
                  >
                    {fact.detail}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Footer Note */}
          <p
            style={{
              textAlign: 'center',
              marginTop: '2rem',
              fontSize: '0.85rem',
              color: 'var(--color-fg-secondary)',
              opacity: 0.8
            }}
          >
            All features available in your browser - no installation required
          </p>
        </div>
      </section>
    </>
  );
}
